import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { syncAll, getToken, saveUserData, getUserData, stripBase64FromNote, subscribeUserDataChanges, CLIENT_ID } from './lib/authSync';
import { loadCachedImages, saveCachedImages, clearCachedImages } from './lib/imageCache';
import { isLocalMode, setStorageMode, loadLocalWorkspace, saveLocalWorkspace } from './lib/storageMode';
import Header from './components/Header';
import Canvas from './components/Canvas';
import FolderCanvas from './components/FolderCanvas';
import ToolStrip from './components/ToolStrip';
import NameInputModal from './components/NameInputModal';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './components/ui/dialog';
import { Button } from './components/ui/button';
import { Badge } from './components/ui/badge';
import { ScrollArea } from './components/ui/scroll-area';
import { Trash2, RotateCcw } from 'lucide-react';
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:10000';
// Cache key for the IndexedDB image store when there's no logged-in userId
// (see imageCache.js) — local-mode images are base64 data URLs, which
// localStorage can't hold, so they live here instead.
const LOCAL_MODE_CACHE_ID = 'local-mode';
// Flat per-image byte estimate used by the storage-usage meter — see usage below.
const FLAT_IMAGE_BYTES = 100 * 1024;
export const fetchNotes = async () => {
  const response = await fetch(`${API_BASE_URL}/api/auth/login`);
  return response.json();
};
function App() {
  const navigate = useNavigate();
  const [notes, setNotes] = useState([]);
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState(null);
  const ALL_ITEMS_FOLDER_ID = '__ALL_ITEMS__';
  const [stickers, setStickers] = useState([]);
  const [noteStickers, setNoteStickers] = useState([]);
  const [images, setImages] = useState([]);
  const [tables, setTables] = useState([]);
  const [todos, setTodos] = useState([]);
  const [sources, setSources] = useState([]);
  const [connections, setConnections] = useState([]);
  const [stats, setStats] = useState({
    totalNotes: 0,
    totalWords: 0,
    xp: 0,
    level: 1,
    timeSpent: 0
  });
  const [showNameInput, setShowNameInput] = useState(false);
  const [userName, setUserName] = useState('Adventurer');
  const [userIcon, setUserIcon] = useState('⭐');
  const [drawingTool, setDrawingTool] = useState(null); // 'freehand' | 'line' | 'arrow' | 'ellipse' | null
  const [isPremium, setIsPremium] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [drawings, setDrawings] = useState([]);
  const timeIntervalRef = useRef(null);
  const syncTimerRef = useRef(null);
  const syncInFlightRef = useRef(false);
  const syncDirtyRef = useRef(false);
  const lastSyncAtRef = useRef(0);
  const userDataSaveTimerRef = useRef(null);
  const userDataSaveInFlightRef = useRef(false);
  const userDataDirtyRef = useRef(false);
  const lastUserDataSaveAtRef = useRef(0);
  const lastSessionUserIdRef = useRef(localStorage.getItem('userId'));
  const [syncUiState, setSyncUiState] = useState('idle'); // idle | syncing | synced | error
  const [syncUiText, setSyncUiText] = useState('');
  const syncUiTimerRef = useRef(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [trash, setTrash] = useState([]);
  const isDrawingMode = !!drawingTool;
  const [isTrashOpen, setIsTrashOpen] = useState(false);
  const [activeNoteId, setActiveNoteId] = useState(null);
  const [noteToolsKey, setNoteToolsKey] = useState(0);
  const [readerModeNoteId, setReaderModeNoteId] = useState(null);
  const [showFolderView, setShowFolderView] = useState(false);
  const [activeFolderItemId, setActiveFolderItemId] = useState(null);
  const [folderToolsKey, setFolderToolsKey] = useState(0);
  const safePosition = (pos) =>
  pos && typeof pos.x === 'number' && typeof pos.y === 'number'
    ? pos
    : { x: 100, y: 100 };

  const safeSize = (size, fallback) =>
  size && typeof size.width === 'number' && typeof size.height === 'number'
    ? size
    : fallback;


  // Undo/Redo state
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);
  const isUndoRedo = useRef(false);
  const historyIndexRef = useRef(-1);

  useEffect(() => {
    historyIndexRef.current = historyIndex;
  }, [historyIndex]);

  useEffect(() => {
    window.gtag?.("event", "page_view", {
      page_path: "/app",
      page_title: "App",
    });
  }, []);

  const safeSet = (key, value) => {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (e) {
      console.warn('localStorage setItem failed for', key, e?.name || e);
      return false;
    }
  };

  const showSyncing = useCallback((text = 'Syncing...') => {
    if (syncUiTimerRef.current) clearTimeout(syncUiTimerRef.current);
    setSyncUiState('syncing');
    setSyncUiText(text);
  }, []);

  const showSynced = useCallback(() => {
    if (syncUiTimerRef.current) clearTimeout(syncUiTimerRef.current);
    setSyncUiState('synced');
    setSyncUiText('All changes synced');
    syncUiTimerRef.current = setTimeout(() => {
      setSyncUiState('idle');
      setSyncUiText('');
    }, 2000);
  }, []);

  const showSyncError = useCallback((text = 'Sync failed (will retry)') => {
    if (syncUiTimerRef.current) clearTimeout(syncUiTimerRef.current);
    setSyncUiState('error');
    setSyncUiText(text);
    syncUiTimerRef.current = setTimeout(() => {
      setSyncUiState('idle');
      setSyncUiText('');
    }, 4000);
  }, []);


  // Load data on mount. Local mode reads its own IndexedDB workspace, kept
  // entirely separate from the shared localStorage keys account/sync mode
  // uses — so logging in/out never touches it. (Images are handled by a
  // separate IndexedDB cache, see the "Warm image cache" effect below.)
useEffect(() => {
  const load = (key) => {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : [];
  };

  // Legacy path: shared localStorage keys, used for account/sync mode and
  // as a one-time migration source the first time local mode runs (so
  // in-progress local data isn't orphaned by the switch to IndexedDB).
  const hydrateFromLegacyKeys = () => {
    const token = getToken();
    const notesKey = token && localStorage.getItem('userNotes') ? 'userNotes' : 'anotequest_notes';

    setNotes(
      load(notesKey).map((n) => ({
        ...n,
        position: safePosition(n.position),
      }))
    );

    setFolders(load('anotequest_folders'));

    setStickers(
      load('anotequest_stickers').map(s => ({
        ...s,
        position: safePosition(s.position),
        rotation: s.rotation ?? 0
      }))
    );

    setNoteStickers(
      load('anotequest_note_stickers').map(s => ({
        ...s,
        position: safePosition(s.position),
        size: safeSize(s.size, { width: 200, height: 160 }),
        paths: s.paths || [],
      }))
    );

    setImages(
      load('anotequest_images').map(i => ({
        ...i,
        position: safePosition(i.position),
        size: safeSize(i.size, { width: 300, height: 200 })
      }))
    );

    setSources(
      load('anotequest_sources').map(s => ({
        ...s,
        position: safePosition(s.position),
        size: safeSize(s.size, { width: 260, height: 120 }),
      }))
    );

    setTables(
      load('anotequest_tables').map(t => ({
        ...t,
        position: safePosition(t.position),
        size: safeSize(t.size, { width: 400, height: 200 }),
        data: t.data ?? [['']]
      }))
    );

    setTodos(
      load('anotequest_todos').map(t => ({
        ...t,
        position: safePosition(t.position)
      }))
    );

    setDrawings(load('anotequest_drawings'));

    const savedTrash = localStorage.getItem('anotequest_trash');
    if (savedTrash) setTrash(JSON.parse(savedTrash));

    const savedStats = localStorage.getItem('anotequest_stats');
    if (savedStats) setStats(JSON.parse(savedStats));

    const savedPremium = localStorage.getItem('anotequest_premium');
    if (savedPremium) setIsPremium(JSON.parse(savedPremium));

    const savedUserName = localStorage.getItem('anotequest_username');
    if (savedUserName) setUserName(savedUserName);
    else setShowNameInput(true);

    const savedUserIcon = localStorage.getItem('anotequest_user_icon');
    if (savedUserIcon) setUserIcon(savedUserIcon);
  };

  const hydrateFromWorkspace = (w) => {
    setNotes((w.notes || []).map((n) => ({ ...n, position: safePosition(n.position) })));
    setFolders(w.folders || []);
    setStickers((w.stickers || []).map((s) => ({
      ...s,
      position: safePosition(s.position),
      rotation: s.rotation ?? 0,
    })));
    setNoteStickers((w.noteStickers || []).map((s) => ({
      ...s,
      position: safePosition(s.position),
      size: safeSize(s.size, { width: 200, height: 160 }),
      paths: s.paths || [],
    })));
    setSources((w.sources || []).map((s) => ({
      ...s,
      position: safePosition(s.position),
      size: safeSize(s.size, { width: 260, height: 120 }),
    })));
    setTables((w.tables || []).map((t) => ({
      ...t,
      position: safePosition(t.position),
      size: safeSize(t.size, { width: 400, height: 200 }),
      data: t.data ?? [['']],
    })));
    setTodos((w.todos || []).map((t) => ({ ...t, position: safePosition(t.position) })));
    setDrawings(w.drawings || []);
    setTrash(w.trash || []);
    if (w.stats) setStats(w.stats);
    if (w.username) setUserName(w.username);
    else setShowNameInput(true);
    if (w.userIcon) setUserIcon(w.userIcon);
  };

  if (isLocalMode()) {
    (async () => {
      try {
        const workspace = await loadLocalWorkspace();
        if (workspace) {
          hydrateFromWorkspace(workspace);
        } else {
          // First run of local mode on this device — carry over whatever's
          // already sitting in localStorage instead of showing a blank
          // canvas, then persist it as the real local workspace from now on.
          hydrateFromLegacyKeys();
        }
      } catch (err) {
        console.error('Failed to load local workspace, starting fresh', err);
      } finally {
        setIsLoaded(true);
      }
    })();
    return;
  }

  try {
    hydrateFromLegacyKeys();
    setIsLoaded(true);
  } catch (err) {
    console.error('Corrupted localStorage, resetting', err);
    localStorage.clear();
    setIsLoaded(true);
  }
}, []);

  // Applies a userData snapshot fetched from the server to React state.
  // force=false (initial load): only fills a field when localStorage has
  // nothing for it yet, so we don't clobber unsynced local edits on mount.
  // force=true (realtime remote-change push): always applies, since the
  // whole point there is that another tab/device just saved fresher data.
  const applyServerUserData = useCallback((userData, { force = false } = {}) => {
    const shouldHydrateKey = (key, serverValue) => {
      if (force) return true;
      const raw = localStorage.getItem(key);
      if (raw == null) return true;
      if (raw === '[]' && Array.isArray(serverValue) && serverValue.length > 0) return true;
      if (raw === '{}' && serverValue && typeof serverValue === 'object' && Object.keys(serverValue).length > 0) return true;
      return false;
    };
    {
      if (shouldHydrateKey('anotequest_notes', userData.notes) && Array.isArray(userData.notes)) {
        const hydratedNotes = userData.notes.map((n) => ({
          ...n,
          position: safePosition(n.position),
        }));
        setNotes(hydratedNotes);
        safeSet('anotequest_notes', JSON.stringify(hydratedNotes));
        safeSet('userNotes', JSON.stringify(hydratedNotes));
      }

      // Only fill from server when localStorage is missing (quota can prevent saving images)
      if (shouldHydrateKey('anotequest_images', userData.images) && Array.isArray(userData.images)) {
        setImages(userData.images.map((i) => ({
          ...i,
          position: safePosition(i.position),
          size: safeSize(i.size, { width: 300, height: 200 }),
        })));
        const userId = localStorage.getItem('userId');
        if (userId) saveCachedImages(userId, userData.images);
      }

      if (shouldHydrateKey('anotequest_todos', userData.todos) && Array.isArray(userData.todos)) {
        setTodos(userData.todos.map((t) => ({ ...t, position: safePosition(t.position) })));
      }

      if (shouldHydrateKey('anotequest_note_stickers', userData.note_stickers) && Array.isArray(userData.note_stickers)) {
        setNoteStickers(userData.note_stickers.map((ns) => ({
          ...ns,
          position: safePosition(ns.position),
          size: safeSize(ns.size, { width: 200, height: 160 }),
          paths: ns.paths || [],
        })));
      }

      if (shouldHydrateKey('anotequest_sources', userData.sources) && Array.isArray(userData.sources)) {
        setSources(userData.sources.map((src) => ({
          ...src,
          position: safePosition(src.position),
          size: safeSize(src.size, { width: 260, height: 120 }),
        })));
      }

      if (shouldHydrateKey('anotequest_stickers', userData.stickers) && Array.isArray(userData.stickers)) {
        setStickers(userData.stickers.map((st) => ({
          ...st,
          position: safePosition(st.position),
        })));
      }

      if (shouldHydrateKey('anotequest_tables', userData.tables) && Array.isArray(userData.tables)) {
        setTables(userData.tables.map((t) => ({
          ...t,
          position: safePosition(t.position),
          size: safeSize(t.size, { width: 400, height: 200 }),
          data: t.data ?? [['']],
        })));
      }

      if (shouldHydrateKey('anotequest_drawings', userData.drawings) && Array.isArray(userData.drawings)) {
        setDrawings(userData.drawings);
      }

      if (shouldHydrateKey('anotequest_trash', userData.trash) && Array.isArray(userData.trash)) {
        setTrash(userData.trash);
      }

      if (shouldHydrateKey('anotequest_folders', userData.folders) && Array.isArray(userData.folders)) {
        setFolders(userData.folders);
      }

      if (shouldHydrateKey('anotequest_stats', userData.stats) && userData.stats) {
        setStats(userData.stats);
      }

      if (shouldHydrateKey('anotequest_username', userData.username) && typeof userData.username === 'string') {
        setUserName(userData.username);
      }
    }
  }, [setNotes, setImages, setTodos, setNoteStickers, setSources, setStickers, setTables, setDrawings, setTrash, setFolders, setStats, setUserName]);

  const hydrateFromServer = useCallback(async () => {
    if (!getToken()) return;
    try {
      const { userData, isPremium: serverPremium } = await getUserData();
      if (!userData) return;

      setIsPremium(serverPremium);
      safeSet('anotequest_premium', JSON.stringify(serverPremium));
      applyServerUserData(userData, { force: false });
    } catch (e) {
      console.warn('Failed to hydrate from server:', e?.message || e);
    }
  }, [applyServerUserData, setIsPremium]);

  useEffect(() => {
    if (!isLoaded) return;
    if (!getToken()) return;
    hydrateFromServer();
  }, [isLoaded, hydrateFromServer]);

  // Warm image cache from IndexedDB for faster reloads (localStorage can't hold base64 images)
  useEffect(() => {
    if (!isLoaded) return;
    const token = getToken();
    if (!token && !isLocalMode()) return;

    const userId = token ? localStorage.getItem('userId') : LOCAL_MODE_CACHE_ID;
    if (!userId) return;

    (async () => {
      const cached = await loadCachedImages(userId);
      if (cached && cached.length) {
        setImages(
          cached.map((i) => ({
            ...i,
            position: safePosition(i.position),
            size: safeSize(i.size, { width: 300, height: 200 }),
          }))
        );
      }
    })();
  }, [isLoaded]);

  // One-time migration: convert any legacy JPEG/PNG images to WebP + resize to max 1200px
  useEffect(() => {
    if (!isLoaded) return;
    const MIGRATION_KEY = 'anotequest_webp_v2';
    if (localStorage.getItem(MIGRATION_KEY)) return;

    const MAX_DIM = 1200;
    const convertToWebP = (dataUrl) => new Promise((resolve) => {
      if (!dataUrl || !dataUrl.startsWith('data:image/')) {
        resolve(dataUrl);
        return;
      }
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > MAX_DIM || height > MAX_DIM) {
          if (width >= height) { height = Math.round(height * MAX_DIM / width); width = MAX_DIM; }
          else { width = Math.round(width * MAX_DIM / height); height = MAX_DIM; }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/webp', 0.80));
      };
      img.onerror = () => resolve(dataUrl);
      img.src = dataUrl;
    });

    (async () => {
      // Migrate standalone canvas images
      const needsImageMigration = images.some(i => i.data && !i.data.startsWith('data:image/webp'));
      if (needsImageMigration) {
        const converted = await Promise.all(
          images.map(async (i) => i.data && !i.data.startsWith('data:image/webp')
            ? { ...i, data: await convertToWebP(i.data) }
            : i
          )
        );
        setImages(converted);
      }

      // Migrate images embedded in notes
      const needsNotesMigration = notes.some(n =>
        n.images?.some(i => i.data && !i.data.startsWith('data:image/webp'))
      );
      if (needsNotesMigration) {
        const convertedNotes = await Promise.all(
          notes.map(async (n) => {
            if (!n.images?.some(i => i.data && !i.data.startsWith('data:image/webp'))) return n;
            const imgs = await Promise.all(
              n.images.map(async (i) => i.data && !i.data.startsWith('data:image/webp')
                ? { ...i, data: await convertToWebP(i.data) }
                : i
              )
            );
            return { ...n, images: imgs };
          })
        );
        setNotes(convertedNotes);
      }

      localStorage.setItem(MIGRATION_KEY, '1');
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoaded]);

  // Persist images to IndexedDB whenever they change (best-effort)
  useEffect(() => {
    if (!isLoaded) return;
    const token = getToken();
    if (!token && !isLocalMode()) return;
    const userId = token ? localStorage.getItem('userId') : LOCAL_MODE_CACHE_ID;
    if (!userId) return;
    // Don't block UI; just cache
    saveCachedImages(userId, images);
  }, [images, isLoaded]);

  const buildLocalWorkspaceSnapshot = useCallback(() => ({
    username: userName,
    userIcon,
    stats,
    folders,
    notes,
    noteStickers,
    todos,
    sources,
    stickers,
    tables,
    drawings,
    trash,
  }), [userName, userIcon, stats, folders, notes, noteStickers, todos, sources, stickers, tables, drawings, trash]);

  // Local mode's own persistence — debounced write to its dedicated IndexedDB
  // workspace, entirely separate from the shared localStorage keys so it's
  // never touched by account login/logout. Images persist separately (see
  // "Persist images to IndexedDB" above).
  const localWorkspaceSaveTimerRef = useRef(null);
  useEffect(() => {
    if (!isLoaded) return;
    if (!isLocalMode()) return;

    if (localWorkspaceSaveTimerRef.current) clearTimeout(localWorkspaceSaveTimerRef.current);
    localWorkspaceSaveTimerRef.current = setTimeout(() => {
      saveLocalWorkspace(buildLocalWorkspaceSnapshot());
    }, 1000);

    return () => clearTimeout(localWorkspaceSaveTimerRef.current);
  }, [isLoaded, buildLocalWorkspaceSnapshot]);

  // Rough size estimate for the "storage used" indicator in Settings — the
  // same snapshot shape works for both local and synced accounts since it's
  // just measuring current in-memory document data either way. Images aren't
  // part of the snapshot itself (kept out of local-mode's IndexedDB payload),
  // so their weight is added separately as a flat per-image estimate — most
  // uploads land in R2 as compressed WebP in the tens-to-low-hundreds-of-KB
  // range, so a flat number is close enough without tracking real bytes.
  const localDataBytes = useMemo(() => {
    try {
      const documentBytes = new Blob([JSON.stringify(buildLocalWorkspaceSnapshot())]).size;
      const embeddedImageCount = notes.reduce(
        (sum, note) => sum + (Array.isArray(note.images) ? note.images.length : 0),
        0
      );
      const imageBytes = (images.length + embeddedImageCount) * FLAT_IMAGE_BYTES;
      return documentBytes + imageBytes;
    } catch {
      return 0;
    }
  }, [buildLocalWorkspaceSnapshot, images, notes]);

  const handleStorageModeChange = useCallback((mode) => {
    setStorageMode(mode);
    // Switching source of truth (localStorage/server <-> IndexedDB workspace)
    // reloads so the mount effect re-hydrates from the right place instead
    // of trying to reconcile two live state trees.
    window.location.reload();
  }, []);

  // If user changes (different account), wipe in-memory state so we don't push previous user's data
  useEffect(() => {
    if (!isLoaded) return;
    const currentUserId = localStorage.getItem('userId');
    if (!currentUserId) return;
    if (lastSessionUserIdRef.current && lastSessionUserIdRef.current !== currentUserId) {
      console.warn('Detected user change. Resetting in-memory state to prevent cross-account mixing.');
      setNotes([]);
      setFolders([]);
      setStickers([]);
      setNoteStickers([]);
      setImages([]);
      setTables([]);
      setTodos([]);
      setSources([]);
      setDrawings([]);
      setTrash([]);
      setStats({
        totalNotes: 0,
        totalWords: 0,
        xp: 0,
        level: 1,
        timeSpent: 0,
      });
    }
    lastSessionUserIdRef.current = currentUserId;
  }, [isLoaded]);



  // Track time spent
  useEffect(() => {
    if (!isLoaded) return;
    
    timeIntervalRef.current = setInterval(() => {
      setStats(prev => ({
        ...prev,
        timeSpent: prev.timeSpent + 1
      }));
    }, 1000);

    return () => {
      if (timeIntervalRef.current) {
        clearInterval(timeIntervalRef.current);
      }
    };
  }, [isLoaded]);

  // Save to localStorage
  useEffect(() => {
    if (!isLoaded) return;
    const timeout = setTimeout(() => {
      safeSet('anotequest_notes', JSON.stringify(notes));
      if (getToken()) safeSet('userNotes', JSON.stringify(notes));
    }, 500);
    return () => clearTimeout(timeout);
  }, [notes, isLoaded]);

  const buildUserDataSnapshot = useCallback(() => ({
    username: localStorage.getItem('anotequest_username') || userName,
    stats,
    folders,
    notes,
    images,
    note_stickers: noteStickers,
    todos,
    sources,
    stickers,
    tables,
    drawings,
    trash,
    theme: localStorage.getItem('theme') || undefined,
  }), [userName, stats, folders, notes, images, noteStickers, todos, sources, stickers, tables, drawings, trash]);

  const flushUserDataSave = useCallback(async (opts = {}) => {
    if (!getToken()) return false;
    try {
      showSyncing(opts.label || 'Syncing...');
      await saveUserData(buildUserDataSnapshot());
      showSynced();
      return true;
    } catch (e) {
      console.warn('Flush save failed:', e?.message || e);
      showSyncError();
      return false;
    }
  }, [buildUserDataSnapshot, showSyncing, showSynced, showSyncError]);

  // Fired when another tab/device saves this account's data — refetches and
  // applies the fresh snapshot so this tab doesn't sit stale until its own
  // next save. Flushes any unsaved local edits first so they aren't silently
  // dropped by the incoming snapshot (this can't fully prevent a conflict if
  // both tabs edit within the same couple of seconds — there's no per-field
  // merge, just last-write-wins — but it closes the far more common window
  // where a tab sat stale for up to 30s or until logout).
  const applyRemoteUserDataChange = useCallback(async () => {
    if (!getToken()) return;
    try {
      if (userDataDirtyRef.current) {
        await flushUserDataSave({ label: 'Saving...' });
      }
      const { userData, isPremium: serverPremium } = await getUserData();
      if (!userData) return;

      setIsPremium(serverPremium);
      safeSet('anotequest_premium', JSON.stringify(serverPremium));
      applyServerUserData(userData, { force: true });
    } catch (e) {
      console.warn('Failed to apply remote userData change:', e?.message || e);
    }
  }, [applyServerUserData, flushUserDataSave, setIsPremium]);

  // applyRemoteUserDataChange's identity changes on almost every canvas edit
  // (it depends on buildUserDataSnapshot, which depends on nearly all app
  // state) — routing it through a ref keeps this effect from tearing down
  // and reopening the EventSource on every keystroke/drag.
  const applyRemoteUserDataChangeRef = useRef(applyRemoteUserDataChange);
  useEffect(() => {
    applyRemoteUserDataChangeRef.current = applyRemoteUserDataChange;
  }, [applyRemoteUserDataChange]);

  useEffect(() => {
    if (!isLoaded) return;
    if (!getToken()) return;
    const unsubscribe = subscribeUserDataChanges(() => {
      applyRemoteUserDataChangeRef.current();
    });
    return unsubscribe;
  }, [isLoaded]);

  const scheduleUserDataSave = useCallback((reason = 'state-changed') => {
    if (!getToken()) return;
    userDataDirtyRef.current = true;

    if (userDataSaveTimerRef.current) clearTimeout(userDataSaveTimerRef.current);
    userDataSaveTimerRef.current = setTimeout(async () => {
      if (!userDataDirtyRef.current) return;
      if (userDataSaveInFlightRef.current) return;
      if (!getToken()) {
        userDataDirtyRef.current = false;
        return;
      }

      const now = Date.now();
      if (now - lastUserDataSaveAtRef.current < 30_000) {
        scheduleUserDataSave('rate-limit');
        return;
      }

      userDataSaveInFlightRef.current = true;
      try {
        await flushUserDataSave({ label: 'Saving...' });
        lastUserDataSaveAtRef.current = Date.now();
        userDataDirtyRef.current = false;
      } catch (e) {
        console.warn('UserData save failed:', e?.message || e);
      } finally {
        userDataSaveInFlightRef.current = false;
      }
    }, 8000);
  }, [flushUserDataSave]);

  const scheduleServerSync = useCallback((reason = 'notes-changed') => {
    if (!getToken()) return;
    syncDirtyRef.current = true;

    // Debounce changes; never sync more often than every 30s
    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(async () => {
      if (!syncDirtyRef.current) return;
      if (syncInFlightRef.current) return;
      if (!getToken()) {
        syncDirtyRef.current = false;
        return;
      }

      const now = Date.now();
      if (now - lastSyncAtRef.current < 30_000) {
        scheduleServerSync('rate-limit');
        return;
      }

      syncInFlightRef.current = true;
      showSyncing('Syncing...');
      try {
        await saveUserData(buildUserDataSnapshot());
        await syncAll(notes);
        lastSyncAtRef.current = Date.now();
        syncDirtyRef.current = false;
        showSynced();
      } catch (e) {
        // keep dirty so we can retry later
        console.warn('Background sync failed:', e?.message || e);
        showSyncError();
      } finally {
        syncInFlightRef.current = false;
      }
    }, 5000);
  }, [notes, buildUserDataSnapshot, showSyncing, showSynced, showSyncError]);

  // Schedule background sync when notes change (no constant polling)
  useEffect(() => {
    if (!isLoaded) return;
    if (!getToken()) return;
    scheduleServerSync();
    return () => {};
  }, [notes, isLoaded, scheduleServerSync]);

  // Schedule background UserData save when state changes (images/todos/folders/etc)
  useEffect(() => {
    if (!isLoaded) return;
    if (!getToken()) return;

    const handler = () => {
      try {
        const token = getToken();
        if (!token) return;
        const snapshot = buildUserDataSnapshot();
        const cleanedSnapshot = Array.isArray(snapshot?.notes)
          ? { ...snapshot, notes: snapshot.notes.map(stripBase64FromNote) }
          : snapshot;
        const body = JSON.stringify({ userData: cleanedSnapshot });
        fetch('/api/userdata', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + token,
            'X-Client-Id': CLIENT_ID,
          },
          body,
          keepalive: true,
        });
      } catch {
        // ignore
      }
    };

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isLoaded, buildUserDataSnapshot]);

  useEffect(() => {
    if (!isLoaded) return;
    if (!getToken()) return;
    scheduleUserDataSave('state-changed');
  }, [
    isLoaded,
    folders,
    notes,
    images,
    noteStickers,
    todos,
    sources,
    stickers,
    tables,
    drawings,
    trash,
    stats,
    scheduleUserDataSave,
  ]);


  useEffect(() => {
    if (!isLoaded) return;
    safeSet('anotequest_folders', JSON.stringify(folders));
  }, [folders, isLoaded]);

  useEffect(() => {
    if (!isLoaded) return;
    safeSet('anotequest_stickers', JSON.stringify(stickers));
  }, [stickers, isLoaded]);

  useEffect(() => {
    if (!isLoaded) return;
    safeSet('anotequest_note_stickers', JSON.stringify(noteStickers));
  }, [noteStickers, isLoaded]);

  useEffect(() => {
    if (!isLoaded) return;
    // Strip base64 blobs before writing to localStorage — they blow the ~5 MB
    // quota and cause QuotaExceededError on every resize/move. Auth-user images
    // now have URL strings in data; guest images are omitted from localStorage
    // (they remain in React state for the session).
    const storable = images.map((img) =>
      typeof img.data === 'string' && img.data.startsWith('data:')
        ? { ...img, data: null }
        : img
    );
    safeSet('anotequest_images', JSON.stringify(storable));
  }, [images, isLoaded]);

  useEffect(() => {
    if (!isLoaded) return;
    safeSet('anotequest_sources', JSON.stringify(sources));
  }, [sources, isLoaded]);

  useEffect(() => {
    if (!isLoaded) return;
    safeSet('anotequest_tables', JSON.stringify(tables));
  }, [tables, isLoaded]);

  useEffect(() => {
    if (!isLoaded) return;
    safeSet('anotequest_todos', JSON.stringify(todos));
  }, [todos, isLoaded]);

  useEffect(() => {
    if (!isLoaded) return;
    safeSet('anotequest_stats', JSON.stringify(stats));
  }, [stats, isLoaded]);

  useEffect(() => {
    if (!isLoaded) return;
    safeSet('anotequest_trash', JSON.stringify(trash));
  }, [trash, isLoaded]);

  useEffect(() => {
    if (!isLoaded) return;
    safeSet('anotequest_drawings', JSON.stringify(drawings));
  }, [drawings, isLoaded]);

  useEffect(() => {
    if (!isLoaded) return;
    safeSet('anotequest_user_icon', userIcon);
  }, [userIcon, isLoaded]);

  // Save history for undo/redo (board-wide, last 10 steps)
  useEffect(() => {
    if (!isLoaded || isUndoRedo.current) {
      isUndoRedo.current = false;
      return;
    }

    const state = {
      notes: [...notes],
      stickers: [...stickers],
      noteStickers: [...noteStickers],
      images: [...images],
      sources: [...sources],
      tables: [...tables],
      todos: [...todos],
      drawings: [...drawings],
    };

    setHistory(prev => {
      const newHistory = prev.slice(0, historyIndexRef.current + 1);
      newHistory.push(state);
      // Keep only last 10 states
      if (newHistory.length > 10) newHistory.shift();
      return newHistory;
    });
    setHistoryIndex(prev => Math.min(prev + 1, 9));
  }, [notes, stickers, noteStickers, images, sources, tables, todos, drawings, isLoaded]);

  // Undo
  const handleUndo = useCallback(() => {
    if (historyIndex > 0) {
      isUndoRedo.current = true;
      const prevState = history[historyIndex - 1];
      setNotes(prevState.notes || []);
      setStickers(prevState.stickers || []);
      setNoteStickers(prevState.noteStickers || []);
      setImages(prevState.images || []);
      setSources(prevState.sources || []);
      setTables(prevState.tables || []);
      setTodos(prevState.todos || []);
      setDrawings(prevState.drawings || []);
      setHistoryIndex(prev => prev - 1);
    }
  }, [history, historyIndex]);

  // Redo
  const handleRedo = useCallback(() => {
    if (historyIndex < history.length - 1) {
      isUndoRedo.current = true;
      const nextState = history[historyIndex + 1];
      setNotes(nextState.notes || []);
      setStickers(nextState.stickers || []);
      setNoteStickers(nextState.noteStickers || []);
      setImages(nextState.images || []);
      setSources(nextState.sources || []);
      setTables(nextState.tables || []);
      setTodos(nextState.todos || []);
      setDrawings(nextState.drawings || []);
      setHistoryIndex(prev => prev + 1);
    }
  }, [history, historyIndex]);

  // Calculate stats
  useEffect(() => {
    if (!isLoaded) return;

    const totalNotes = notes.length;
    const totalWords = notes.reduce((sum, note) => {
      const wordCount = note.content?.trim().split(/\s+/).filter(w => w.length > 0).length || 0;
      return sum + wordCount;
    }, 0);

    const xp = (totalNotes * 10) + Math.floor(totalWords / 10);
    const level = Math.floor(xp / 100) + 1;

    setStats(prev => ({
      ...prev,
      totalNotes,
      totalWords,
      xp,
      level
    }));
  }, [notes, isLoaded]);

  const handleNameSubmit = useCallback((name) => {
    setUserName(name);
    safeSet('anotequest_username', name);
    setShowNameInput(false);
  }, []);

  const addNote = useCallback((note) => {
    if (!isPremium && notes.length >= 100) {
      return { error: 'Free tier limited to 100 notes!' };
    }

    // Place new notes near the last one so they feel predictable, not random
    const lastNote = notes[notes.length - 1];
    const defaultPosition = lastNote?.position
      ? { x: lastNote.position.x + 40, y: lastNote.position.y + 40 }
      : { x: 200, y: 160 };

    const basePosition = note?.position || defaultPosition;

    const newNote = {
      id: Date.now(),
      title: 'New Note',
      content: '',
      ...note,
      position: basePosition,
      createdAt: new Date().toISOString(),
      images: note?.images || [],
      folderId: activeFolder || null
    };
    setNotes(prev => [...prev, newNote]);
    return { success: true, note: newNote };
  }, [isPremium, notes, activeFolder]);

  const updateNote = useCallback((id, updates) => {
    setNotes(prev => prev.map(note => 
      note.id === id ? { ...note, ...updates } : note
    ));
  }, []);

  const addToTrash = useCallback((type, item) => {
    setTrash(prev => [
      ...prev,
      {
        id: Date.now() + Math.random(),
        type,
        item,
        deletedAt: new Date().toISOString(),
      },
    ]);
  }, []);

  const deleteNote = useCallback((id) => {
    setNotes(prev => {
      const note = prev.find(n => n.id === id);
      if (note) addToTrash('note', note);
      return prev.filter(n => n.id !== id);
    });
  }, [addToTrash]);

  const addFolder = useCallback((name) => {
    const newFolder = {
      id: Date.now(),
      name,
      createdAt: new Date().toISOString()
    };
    setFolders(prev => [...prev, newFolder]);
  }, []);

  const updateFolder = useCallback((folderId, updates) => {
    setFolders(prev => prev.map(f => f.id === folderId ? { ...f, ...updates } : f));
  }, []);

  const deleteFolder = useCallback((folderId) => {
    // Remove the folder itself
    setFolders(prev => prev.filter(f => f.id !== folderId));

    // Move items in that folder back to root (no folder)
    setNotes(prev => prev.map(n => n.folderId === folderId ? { ...n, folderId: null } : n));
    setStickers(prev => prev.map(s => s.folderId === folderId ? { ...s, folderId: null } : s));
    setImages(prev => prev.map(i => i.folderId === folderId ? { ...i, folderId: null } : i));
    setSources(prev => prev.map(s => s.folderId === folderId ? { ...s, folderId: null } : s));
    setTables(prev => prev.map(t => t.folderId === folderId ? { ...t, folderId: null } : t));
    setTodos(prev => prev.map(t => t.folderId === folderId ? { ...t, folderId: null } : t));
    setConnections(prev => prev.map(c => c.folderId === folderId ? { ...c, folderId: null } : c));

    // If the deleted folder was active, go back to root
    setActiveFolder(prev => (prev === folderId ? null : prev));
  }, []);

  const addSticker = useCallback((sticker) => {
    const newSticker = {
      id: Date.now() + Math.random(),
      ...sticker,
      folderId: activeFolder,
      createdAt: new Date().toISOString()
    };
    setStickers(prev => [...prev, newSticker]);
  }, [activeFolder]);

  // Note sticker handlers (yellow freeform-style sticky that you can draw on)
  const addNoteSticker = useCallback((sticker = {}) => {
    const basePosition = sticker.position || { x: 220, y: 200 };
    const newSticker = {
      id: Date.now() + Math.random(),
      position: basePosition,
      size: sticker.size || { width: 200, height: 160 },
      paths: [],
      folderId: activeFolder,
      createdAt: new Date().toISOString(),
    };
    setNoteStickers(prev => [...prev, newSticker]);
    return newSticker;
  }, [activeFolder]);

  const updateNoteSticker = useCallback((id, updates) => {
    setNoteStickers(prev => prev.map(s => (s.id === id ? { ...s, ...updates } : s)));
  }, []);

  const deleteNoteSticker = useCallback((id) => {
    setNoteStickers(prev => prev.filter(s => s.id !== id));
  }, []);

  const updateSticker = useCallback((id, updates) => {
    setStickers(prev => prev.map(sticker => 
      sticker.id === id ? { ...sticker, ...updates } : sticker
    ));
  }, []);

  const deleteSticker = useCallback((id) => {
    setStickers(prev => {
      const sticker = prev.find(s => s.id === id);
      if (sticker) addToTrash('sticker', sticker);
      return prev.filter(s => s.id !== id);
    });
  }, [addToTrash]);

  // Image handlers
  const addImage = useCallback((image) => {
    const newImage = {
      id: Date.now() + Math.random(),
      ...image,
      folderId: activeFolder,
      createdAt: new Date().toISOString()
    };
    setImages(prev => [...prev, newImage]);
  }, [activeFolder]);

  const updateImage = useCallback((id, updates) => {
    setImages(prev => prev.map(img =>
      img.id === id ? { ...img, ...updates } : img
    ));
  }, []);

  const deleteImage = useCallback((id) => {
    setImages(prev => {
      const img = prev.find(i => i.id === id);
      if (img) addToTrash('image', img);
      return prev.filter(i => i.id !== id);
    });
  }, [addToTrash]);

  // Table handlers
  const addTable = useCallback((table) => {
    const newTable = {
      id: Date.now() + Math.random(),
      ...table,
      folderId: activeFolder,
      createdAt: new Date().toISOString()
    };
    setTables(prev => [...prev, newTable]);
  }, [activeFolder]);

  const updateTable = useCallback((id, updates) => {
    setTables(prev => prev.map(t => 
      t.id === id ? { ...t, ...updates } : t
    ));
  }, []);

  const deleteTable = useCallback((id) => {
    setTables(prev => {
      const tbl = prev.find(t => t.id === id);
      if (tbl) addToTrash('table', tbl);
      return prev.filter(t => t.id !== id);
    });
  }, [addToTrash]);

  // Source / Link handlers
  const addSource = useCallback((source) => {
    if (!source?.url) return;

    const basePosition = source.position || { x: 220, y: 200 };

    const newSource = {
      id: Date.now() + Math.random(),
      url: source.url,
      title: source.title || source.url,
      description: source.description || '',
      position: basePosition,
      size: source.size || { width: 260, height: 120 },
      folderId: activeFolder,
      createdAt: new Date().toISOString(),
    };
    setSources(prev => [...prev, newSource]);
  }, [activeFolder]);

  const updateSource = useCallback((id, updates) => {
    setSources(prev => prev.map(s => (s.id === id ? { ...s, ...updates } : s)));
  }, []);

  const deleteSource = useCallback((id) => {
    setSources(prev => {
      const src = prev.find(s => s.id === id);
      if (src) addToTrash('source', src);
      return prev.filter(s => s.id !== id);
    });
  }, [addToTrash]);

  // Todo handlers
  const addTodo = useCallback((todo) => {
    const newTodo = {
      id: Date.now() + Math.random(),
      ...todo,
      folderId: activeFolder,
      createdAt: new Date().toISOString()
    };
    setTodos(prev => [...prev, newTodo]);
  }, [activeFolder]);

  const updateTodo = useCallback((id, updates) => {
    setTodos(prev => prev.map(t => 
      t.id === id ? { ...t, ...updates } : t
    ));
  }, []);

  const deleteTodo = useCallback((id) => {
    setTodos(prev => {
      const todoItem = prev.find(t => t.id === id);
      if (todoItem) addToTrash('todo', todoItem);
      return prev.filter(t => t.id !== id);
    });
  }, [addToTrash]);

  const restoreTrashItem = useCallback((trashId) => {
    setTrash(prev => {
      const entry = prev.find(t => t.id === trashId);
      if (!entry) return prev;
      const remaining = prev.filter(t => t.id !== trashId);
      const { type, item } = entry;

      switch (type) {
        case 'note':
          setNotes(prevNotes => [...prevNotes, item]);
          break;
        case 'sticker':
          setStickers(prevStickers => [...prevStickers, item]);
          break;
        case 'image':
          setImages(prevImages => [...prevImages, item]);
          break;
        case 'table':
          setTables(prevTables => [...prevTables, item]);
          break;
        case 'todo':
          setTodos(prevTodos => [...prevTodos, item]);
          break;
        case 'source':
          setSources(prevSources => [...prevSources, item]);
          break;
        default:
          break;
      }

      return remaining;
    });
  }, []);

  const deleteTrashItemForever = useCallback((trashId) => {
    setTrash(prev => prev.filter(t => t.id !== trashId));
  }, []);

  // Connection handlers
  const addConnection = useCallback((connection) => {
    const newConnection = {
      ...connection,
      id: Date.now() + Math.random(),
      folderId: activeFolder
    };
    setConnections(prev => [...prev, newConnection]);
  }, [activeFolder]);

  const deleteConnection = useCallback((index) => {
    setConnections(prev => prev.filter((_, i) => i !== index));
  }, []);

  // Filter notes by folder and search
  const filteredNotes = useMemo(() => {
    let result = activeFolder === ALL_ITEMS_FOLDER_ID
      ? notes
      : activeFolder != null
        ? notes.filter((note) => note.folderId === activeFolder)
        : notes.filter((note) => !note.folderId);
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(note => 
        note.title?.toLowerCase().includes(query) ||
        note.content?.toLowerCase().includes(query)
      );
    }
    
    return result;
  }, [notes, activeFolder, searchQuery]);

  // Filter other items by folder (each folder has its own canvas)
  const filteredStickers = useMemo(() => {
    if (activeFolder === ALL_ITEMS_FOLDER_ID) return stickers;
    return activeFolder != null
      ? stickers.filter((st) => st.folderId === activeFolder)
      : stickers.filter((st) => !st.folderId);
  }, [stickers, activeFolder]);

  const filteredImages = useMemo(() => {
    if (activeFolder === ALL_ITEMS_FOLDER_ID) return images;
    return activeFolder != null
      ? images.filter((i) => i.folderId === activeFolder)
      : images.filter((i) => !i.folderId);
  }, [images, activeFolder]);

  const filteredTables = useMemo(() => {
    if (activeFolder === ALL_ITEMS_FOLDER_ID) return tables;
    return activeFolder != null
      ? tables.filter((t) => t.folderId === activeFolder)
      : tables.filter((t) => !t.folderId);
  }, [tables, activeFolder]);

  const filteredSources = useMemo(() => {
    if (activeFolder === ALL_ITEMS_FOLDER_ID) return sources;
    return activeFolder != null
      ? sources.filter((src) => src.folderId === activeFolder)
      : sources.filter((src) => !src.folderId);
  }, [sources, activeFolder]);

  const filteredTodos = useMemo(() => {
    if (activeFolder === ALL_ITEMS_FOLDER_ID) return todos;
    return activeFolder != null
      ? todos.filter((t) => t.folderId === activeFolder)
      : todos.filter((t) => !t.folderId);
  }, [todos, activeFolder]);

  const filteredNoteStickers = useMemo(() => {
    if (activeFolder === ALL_ITEMS_FOLDER_ID) return noteStickers;
    return activeFolder != null
      ? noteStickers.filter((s) => s.folderId === activeFolder)
      : noteStickers.filter((s) => !s.folderId);
  }, [noteStickers, activeFolder]);


  const handleLogout = useCallback(async () => {
    // Local mode has nothing to log out of — never let this clear
    // localStorage while local-only data depends on the storage-mode flag.
    if (isLocalMode()) {
      navigate('/login', { replace: true });
      return;
    }
    try {
      if (getToken()) {
        toast.loading('Syncing...');
        showSyncing('Final sync...');
        await flushUserDataSave({ label: 'Final sync...' });
        await syncAll(notes);
        toast.dismiss();
      }
    } catch (e) {
      toast.dismiss();
      toast.error(e?.message || 'Final sync failed');
    } finally {
      localStorage.clear();
      navigate('/login', { replace: true });
    }
  }, [notes, navigate, showSyncing, flushUserDataSave]);


  if (!isLoaded) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-muted-foreground">Loading AnoteQuest...</div>
      </div>
    );
  }

  return (
    <div className="h-screen overflow-hidden flex flex-col bg-background pt-14">

      <Header
        stats={stats}
        isPremium={isPremium}
        isDrawingMode={isDrawingMode}
        userName={userName}
        userIcon={userIcon}
        setUserIcon={setUserIcon}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onUndo={handleUndo}
        onRedo={handleRedo}
        canUndo={historyIndex > 0}
        canRedo={historyIndex < history.length - 1}
        notes={notes}
        tables={tables}
        todos={todos}
        folders={folders}
        activeFolder={activeFolder}
        setActiveFolder={setActiveFolder}
        addFolder={addFolder}
        deleteFolder={deleteFolder}
        showFolderView={showFolderView}
        setShowFolderView={setShowFolderView}
      onLogout={handleLogout}
      onNameChange={(newName) => {
        setUserName(newName);
        localStorage.setItem('anotequest_username', newName);
      }}
      localMode={isLocalMode()}
      localDataBytes={localDataBytes}
      onStorageModeChange={handleStorageModeChange}
      />
      
      <div className="flex-1 flex overflow-hidden relative">
        {/* Floating Tool Strip */}
        <ToolStrip
          onAddNote={() => addNote()}
          drawingTool={drawingTool}
          setDrawingTool={setDrawingTool}
          addSticker={addSticker}
          onAddSource={addSource}
          addNoteSticker={addNoteSticker}
          folders={folders}
          notes={notes}
          onAddImage={addImage}
          onAddTable={addTable}
          onAddTodo={addTodo}
          activeNote={notes.find(n => n.id === activeNoteId)}
          onClearActiveNote={() => setActiveNoteId(null)}
          updateNote={updateNote}
          deleteNote={deleteNote}
          onOpenReaderMode={(noteId) => setReaderModeNoteId(noteId)}
          noteToolsKey={noteToolsKey}
          showFolderView={showFolderView}
          onAddFolder={addFolder}
          activeFolder={folders.find(f => f.id === activeFolderItemId)}
          updateFolder={updateFolder}
          deleteFolder={deleteFolder}
          onClearActiveFolder={() => setActiveFolderItemId(null)}
          folderToolsKey={folderToolsKey}
        />

        {/* Canvas slot — either folder view or normal canvas */}
        <div className="flex-1 relative overflow-hidden">
          {showFolderView ? (
            <FolderCanvas
              folders={folders}
              notes={notes}
              updateFolder={updateFolder}
              deleteFolder={deleteFolder}
              onOpenFolderTools={(folderId) => {
                setActiveFolderItemId(folderId);
                setFolderToolsKey(k => k + 1);
              }}
              onEnterFolder={(folderId) => {
                setActiveFolder(folderId);
                setShowFolderView(false);
              }}
            />
          ) : (
          <Canvas
            notes={filteredNotes}
            totalNoteCount={notes.length}
            stickers={filteredStickers}
            noteStickers={filteredNoteStickers}
            images={filteredImages}
            tables={filteredTables}
            todos={filteredTodos}
            sources={filteredSources}
            drawings={drawings}
            setDrawings={setDrawings}
            drawingTool={drawingTool}
            addNote={addNote}
            updateNote={updateNote}
            deleteNote={deleteNote}
            addSticker={addSticker}
            updateSticker={updateSticker}
            deleteSticker={deleteSticker}
            updateNoteSticker={updateNoteSticker}
            deleteNoteSticker={deleteNoteSticker}
            addImage={addImage}
            updateImage={updateImage}
            deleteImage={deleteImage}
            addTable={addTable}
            updateTable={updateTable}
            deleteTable={deleteTable}
            addTodo={addTodo}
            updateTodo={updateTodo}
            deleteTodo={deleteTodo}
            updateSource={updateSource}
            deleteSource={deleteSource}
            folders={folders}
            isPremium={isPremium}
            isDrawingMode={isDrawingMode}
            onCloseDrawing={() => setDrawingTool(null)}
            userName={userName}
            activeFolder={activeFolder}
            onOpenTrash={() => setIsTrashOpen(true)}
            onNoteClick={(note) => setActiveNoteId(note.id)}
            onOpenNoteTools={(note) => { setActiveNoteId(note.id); setNoteToolsKey(k => k + 1); }}
            readerModeNoteId={readerModeNoteId}
            onReaderModeClosed={() => setReaderModeNoteId(null)}
          />
          )}
        </div>
        
        {(syncUiState === 'syncing' || syncUiState === 'synced' || syncUiState === 'error') && (
        <div
          data-anotequest-sync-indicator
          className={`fixed bottom-4 right-4 z-50 rounded-md px-3 py-2 text-sm shadow-lg border ${
            syncUiState === 'error'
              ? 'bg-destructive text-destructive-foreground border-destructive/40'
              : syncUiState === 'synced'
                ? 'bg-emerald-600 text-white border-emerald-700/40'
                : 'bg-card text-foreground border-border'
          }`}
        >
          {syncUiText || (syncUiState === 'syncing' ? 'Syncing...' : syncUiState === 'synced' ? 'All changes synced' : 'Sync failed')}
        </div>
      )}

      </div>

      {/* Modals */}
      {showNameInput && (
        <NameInputModal onComplete={handleNameSubmit} />
      )}
      {/* Trash modal for restoring or permanently deleting items */}
      <Dialog open={isTrashOpen} onOpenChange={setIsTrashOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trash2 className="h-4 w-4" />
              Trash
            </DialogTitle>
            <DialogDescription>
              Recently removed items. Restore them to the canvas or delete them forever.
            </DialogDescription>
          </DialogHeader>

          {trash.length === 0 ? (
            <p className="text-sm text-muted-foreground">Trash is empty.</p>
          ) : (
            <ScrollArea className="max-h-64 mt-2">
              <div className="space-y-3 pr-2">
                {trash
                  .slice()
                  .reverse()
                  .map((entry) => (
                    <div
                      key={entry.id}
                      className="flex items-center justify-between rounded-md border border-border bg-card/80 px-3 py-2"
                    >
                      <div className="flex flex-col text-sm">
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs capitalize">
                            {entry.type}
                          </Badge>
                          <span className="font-medium truncate max-w-[180px]">
                            {entry.type === 'note'
                              ? entry.item.title || 'Untitled note'
                              : entry.type === 'todo'
                              ? entry.item.title || 'Todo list'
                              : entry.type === 'table'
                              ? 'Table'
                              : entry.type === 'image'
                              ? 'Image'
                              : 'Sticker'}
                          </span>
                        </div>
                        <span className="text-[11px] text-muted-foreground">
                          Removed {new Date(entry.deletedAt).toLocaleString()}
                        </span>
                      </div>

                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-xs"
                          onClick={() => restoreTrashItem(entry.id)}
                        >
                          <RotateCcw className="h-3 w-3 mr-1" />
                          Restore
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          className="h-7 px-2 text-xs"
                          onClick={() => deleteTrashItemForever(entry.id)}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))}
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default App;